import React, { useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import Container from "../components/common/Container";

/**
 * OAuthLinkCallback Page
 * 
 * Handles the OAuth callback when linking accounts (GitHub, ORCID) to an existing profile.
 * This is separate from the login OAuth flow.
 * 
 * Flow:
 * 1. User clicks "Link ORCID" → redirects to backend /auth/link/orcid
 * 2. Backend redirects to ORCID OAuth page
 * 3. ORCID redirects here with code and state
 * 4. We forward the request to backend /auth/link/:provider/callback
 * 5. Backend processes and redirects to /profile with success/error
 */
const OAuthLinkCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { provider } = useParams();

  useEffect(() => {
    const processLinkCallback = () => {
      // Get OAuth response params
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");

      if (!provider) {
        setTimeout(() => navigate("/profile?linkError=invalid_provider"), 2000);
        return;
      }

      // Build the backend callback URL
      const apiBaseUrl = process.env.REACT_APP_BASE_URL;
      const backendCallbackUrl = `${apiBaseUrl}/auth/link/${provider}/callback`;
      
      // Forward all query params to the backend
      const params = new URLSearchParams();
      if (code) params.append("code", code);
      if (state) params.append("state", state);
      if (error) params.append("error", error);

      // Redirect to backend to complete OAuth flow
      window.location.href = `${backendCallbackUrl}?${params.toString()}`;
    };

    processLinkCallback();
  }, [provider, searchParams, navigate]);

  return (
    <Container className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Oval
          height={64}
          width={64}
          color="#792CF3"
          secondaryColor="#e5e7eb"
          strokeWidth={4}
          wrapperClass="justify-center mb-6"
        />
        <h2 className="text-2xl font-bold text-secondary mb-2">
          Linking Your Account...
        </h2>
        <p className="text-gray-500">
          Please wait while we complete the verification.
        </p>
      </div>
    </Container>
  );
};

export default OAuthLinkCallback;
